import { getCheckoutForSite, activeSiteCountry } from "@config/site/site-config";
import type {
    Address,
    Cart,
    CustomFieldsType,
    EccoAddDeliveryAddressToMyCartMutationVariables,
    Order,
    ShippingMethod,
} from "@graphql/generated/types";
import { countryCodeLookup } from "@lib/utils/countryUtility";
import type { CheckoutFns } from "./checkout-utility/checkoutUtility";
import type { StoreData } from "@components/store-locator/controller";
import { ShippingMethodTypes } from "@components/commerce/checkout/types";
import { isEmpty } from "lodash-es";
import { AdyenStrategy } from "./payment-methods/adyen-strategy/adyenStrategy";
import { PaymentProvider } from "./payment-methods/paymentProvider";
import type { CountryCode } from "@config/site/types";
import { getDeliveryMethodKey } from "./findDeliveryMethods";
import { formatPhoneNumberByCountry } from "./phoneNumber";
import { AmazonStrategy } from "./payment-methods/amazon-strategy/amazonStrategy";
import type { IPaymentStrategy } from "@interfaces/payment-method/PaymentStrategy";
import { AMAZON_QUERY_PARAMS } from "./checkout-utility/amazon-pay/constants";
import type { CheckoutFormValues } from "@components/commerce/checkout/delivery-step/types";

export type DeliveryInitialValues = Partial<EccoAddDeliveryAddressToMyCartMutationVariables> &
    ExpandedFields & {
        initialized?: boolean;
        isCompany?: boolean;
        company?: string;
        salutation?: string;
    };

export type ContactDetailsInitialValues = {
    email: string;
    phone: string;
    countryCode: string;
    policy: boolean;
    isSubscribed: boolean;
    initialized: boolean;
};

export type BillingInitialValues = {
    firstName?: string;
    lastName?: string;
    company?: string;
    streetName?: string;
    additionalAddressInfo?: string;
    postalCode?: string;
    city?: string;
    country?: CountryCode | "";
    region?: string;
    isCompany?: boolean;
    salutation?: string;
    customerNumber?: string;
};

export type ExpandedFields = {
    phoneticFirstName?: string;
    phoneticLastName?: string;
    deliveryInstructions?: string;
    salutation?: string;
    customerNumber?: string;
};

export type PaymentValues = {
    paymentSystem?: string;
    paymentSystemIcon?: string;
    paymentReference?: string;
    paymentDateOfExpiry?: string;
};

type DeliveryMethodValues = Partial<ShippingMethod>;

type deliveryDatesAndTimeSlots = {
    date?: string;
    timeslot?: string;
};

export type OrderDetails = {
    deliveryMethod: DeliveryMethodValues;
    deliveryAddress: DeliveryInitialValues;
    billingAddress: BillingInitialValues;
    paymentMethod: PaymentValues;
    contactDetailsValues: ContactDetailsInitialValues;
    hasSameBillingAddress: boolean;
};

export const initialContactDetailsValues: ContactDetailsInitialValues = {
    email: "",
    phone: "",
    countryCode: "",
    policy: false,
    initialized: false,
    isSubscribed: false,
};

const initialDeliveryAddressValues: DeliveryInitialValues &
    ExpandedFields &
    deliveryDatesAndTimeSlots = {
    firstName: "",
    lastName: "",
    company: "",
    streetName: "",
    additionalAddressInfo: "",
    postalCode: "",
    city: "",
    country: "",
    initialized: false,
    region: "",
    date: "",
    timeslot: "",
    phoneticFirstName: null,
    phoneticLastName: null,
    isCompany: false,
    salutation: "",
    customerNumber: "",
};

const initialBillingAddressValues: BillingInitialValues = {
    firstName: "",
    lastName: "",
    company: "",
    streetName: "",
    additionalAddressInfo: "",
    postalCode: "",
    city: "",
    country: "",
    region: "",
    isCompany: false,
    salutation: "",
    customerNumber: "",
};

const deliveryMethodValues: DeliveryMethodValues = {
    key: "",
    name: "",
};

// This function has return value of actual checkout form
export const initializeFormValues = (
    cart: Readonly<Cart>,
    variables: Readonly<CheckoutFns>,
    storeData: Readonly<StoreData>
): Readonly<CheckoutFormValues> => {
    const { customer, shippingAddress, billingAddress, shippingInfo } = cart;
    const contactDetails = structuredClone(initialContactDetailsValues);
    const deliveryAddressValues = structuredClone(initialDeliveryAddressValues);
    const billingAddressValues = structuredClone(initialBillingAddressValues);

    if (shippingAddress) {
        const contactValues = toContactDetails(shippingAddress, customer);
        for (const key in contactValues) {
            contactDetails[key] = contactValues[key];
        }
        contactDetails.initialized = true;
    }
    if (!contactDetails.countryCode) {
        contactDetails.countryCode = variables.countryCode;
    }

    if (shippingAddress?.company) {
        deliveryAddressValues.isCompany = true;
        deliveryAddressValues.firstName = "";
        deliveryAddressValues.lastName = "";
    }

    if (billingAddress?.company) {
        billingAddressValues.isCompany = true;
        billingAddressValues.firstName = "";
        billingAddressValues.lastName = "";
    }

    deliveryAddressValues.country = shippingAddress?.country ?? variables.country;
    if (shippingAddress?.streetName || shippingAddress?.region) {
        addressToValues(deliveryAddressValues, shippingAddress);
        deliveryAddressValues.initialized = true;
    }

    if (billingAddress?.streetName) {
        addressToValues(billingAddressValues, billingAddress);
        deliveryAddressValues.initialized = true;
    }

    const deliveryDatesStoredInCart = findCustomField(cart, "order_CF_DeliveryDate");
    const deliveryTimeslotStoredInCart = findCustomField(cart, "order_CF_DeliveryTimeSlot");
    deliveryAddressValues.date = deliveryDatesStoredInCart || "";
    deliveryAddressValues.timeslot = deliveryTimeslotStoredInCart || "";

    billingAddressValues.salutation = shippingAddress?.salutation || "";

    const billingVatNumber = findCustomField(billingAddress, "address_CF_VatId");

    const deliveryAddressIsCompanyField = findCustomField<boolean | string>(
        shippingAddress,
        "address_CF_IsCompany"
    );
    if (
        typeof deliveryAddressIsCompanyField !== "undefined" &&
        deliveryAddressIsCompanyField !== null
    ) {
        deliveryAddressValues.isCompany =
            deliveryAddressIsCompanyField === "true" || deliveryAddressIsCompanyField === true;
        deliveryAddressValues.firstName = "";
        deliveryAddressValues.lastName = "";
    } else {
        deliveryAddressValues.isCompany = false;
        deliveryAddressValues.company = "";
    }

    const billingAddressIsCompanyField = findCustomField<boolean | string>(
        billingAddress,
        "address_CF_IsCompany"
    );
    if (billingAddressIsCompanyField) {
        billingAddressValues.isCompany =
            billingAddressIsCompanyField === "true" || billingAddressIsCompanyField === true;
        billingAddressValues.firstName = "";
        billingAddressValues.lastName = "";
    } else {
        billingAddressValues.isCompany = false;
        billingAddressValues.company = "";
    }

    if (shippingInfo?.shippingMethod?.key && shippingInfo?.shippingMethod?.name) {
        deliveryMethodValues.key = shippingInfo.shippingMethod?.key;
        deliveryMethodValues.name = shippingInfo.shippingMethod?.name;
    }

    const hasSameBillingAddress = billingAndDeliveryAreEqual({
        billing: billingAddressValues,
        delivery: deliveryAddressValues,
    });

    return {
        initialized: true,
        vatNumber: billingVatNumber,
        contactDetailsValues: contactDetails,
        deliveryAddress: deliveryAddressValues,
        billingAddress: billingAddressValues,
        // When selecting store for click and collect (in StoreLocator), we don't update it in the cart
        // therefore we check if storeData is empty to determine if the delivery method is click and collect
        deliveryMethodId: !isEmpty(storeData)
            ? getDeliveryMethodKey(ShippingMethodTypes.CLICK_AND_COLLECT)
            : deliveryMethodValues.key,
        hasSameBillingAddress,
    };
};

export const orderDetails = async (order: Order): Promise<OrderDetails> => {
    const country = activeSiteCountry;

    // Clone initial values to avoid mutating the initial state
    const contactDetails = structuredClone(initialContactDetailsValues);
    const deliveryAddress = structuredClone(initialDeliveryAddressValues);
    const billingAddress = structuredClone(initialBillingAddressValues);

    if (order.shippingAddress) {
        const contactValues = toContactDetails(order.shippingAddress);
        for (const key in contactValues) {
            contactDetails[key] = contactValues[key];
        }
    }

    const currentCountry = order?.shippingAddress
        ? await countryCodeLookup(order?.shippingAddress?.country, true)
        : null;
    deliveryAddress.country = currentCountry ?? country;

    if (order?.shippingAddress?.firstName || order?.shippingAddress?.company) {
        addressToValues(deliveryAddress, order?.shippingAddress);
        deliveryAddress.initialized = true;
    }

    if (order?.billingAddress?.firstName || order?.billingAddress?.company) {
        addressToValues(billingAddress, order?.billingAddress);
    }

    if (order?.shippingInfo?.shippingMethod?.key && order?.shippingInfo?.shippingMethod?.name) {
        deliveryMethodValues.key = order?.shippingInfo?.shippingMethod?.key;
        deliveryMethodValues.name =
            order?.shippingInfo?.shippingMethod?.localizedName ??
            order?.shippingInfo?.shippingMethod?.name ??
            "";
    }

    deliveryAddress.date = findCustomField(order, "order_CF_DeliveryDate");
    deliveryAddress.timeslot = findCustomField(order, "order_CF_DeliveryTimeSlot");

    const paymentProvider = new PaymentProvider(
        new Map<string, IPaymentStrategy>([
            ["adyen", new AdyenStrategy()],
            ["amazon_pay", new AmazonStrategy()],
            // new provider strategy can be added here, like Amazon, Rakuten etc.
        ])
    );

    const paymentProviderName = getPaymentMethodProviderFromOrder(order);
    const paymentValues = paymentProvider.getPaymentMethod(order, paymentProviderName);

    return {
        contactDetailsValues: contactDetails,
        deliveryAddress: deliveryAddress,
        billingAddress: billingAddress,
        hasSameBillingAddress: billingAndDeliveryAreEqual({
            billing: billingAddress,
            delivery: deliveryAddress,
        }),
        deliveryMethod: deliveryMethodValues,
        paymentMethod: paymentValues,
    };
};

export const billingAndDeliveryAreEqual = ({ billing, delivery }) => {
    return (
        billing.firstName === delivery.firstName &&
        billing.lastName === delivery.lastName &&
        billing.isCompany === delivery.isCompany &&
        billing.company === delivery.company &&
        billing.streetName === delivery.streetName &&
        billing.additionalAddressInfo === delivery.additionalAddressInfo &&
        billing.postalCode === delivery.postalCode &&
        billing.city === delivery.city &&
        (billing.country === "" || billing.country === delivery.country)
    );
};

const toContactDetails = (address: Address, customer?: Cart["customer"]) => {
    const { countryCode } = getCheckoutForSite;
    const values: Record<string, string> = {};

    const phone = getPhoneNumber(address.phone, countryCode);
    const loggedInUserPhone = findCustomField(customer, "customer_CF_PhoneNumber");
    const loggedInUserEmail = customer?.email;

    values.phone = phone || loggedInUserPhone || "";

    values.countryCode = countryCode;
    values.email = address.email || loggedInUserEmail || "";
    return values;
};

const addressToValues = (
    values: DeliveryInitialValues | BillingInitialValues,
    address: Address
) => {
    values.firstName = address.firstName || values.firstName;
    values.lastName = address.lastName || values.lastName;
    values.company = address.company || values.company;
    values.streetName = address.streetName;
    values.additionalAddressInfo = address.additionalAddressInfo;
    values.postalCode = address.postalCode;
    values.city = address.city;
    values.region = address.region;

    // Create separate typescript interface for form values and graphql response
    (values as any).phoneticFirstName = findCustomField(address, "address_CF_PhoneticFirstName");
    (values as any).phoneticLastName = findCustomField(address, "address_CF_PhoneticLastName");
    (values as any).landmark = findCustomField(address, "address_CF_Landmark");
    (values as any).deliveryInstructions = findCustomField(
        address,
        "address_CF_DeliveryInstructions"
    );
    (values as DeliveryInitialValues).customerNumber = findCustomField(
        address,
        "address_CF_CustomerNumber"
    );
    values.salutation = address.salutation;

    (values as any).isCompany =
        (findCustomField(address, "address_CF_IsCompany") ?? false).toString() === "true";

    if (address.country) {
        values.country = address.country;
    }
};

function findCustomField<FieldType = string>(
    address: { custom?: CustomFieldsType },
    fieldName: string
): FieldType | undefined {
    return address?.custom?.customFieldsRaw?.find((field) => field.name === fieldName)
        ?.value as FieldType;
}

export function removeCountryCodeFromPhoneNumber(phoneNumber: string, countryCode: string): string {
    if (phoneNumber?.startsWith(countryCode)) {
        return phoneNumber?.substring(countryCode.length);
    }
    return phoneNumber;
}

function getPhoneNumber(phoneNumber: string, countryCode: string): string {
    const phone = removeCountryCodeFromPhoneNumber(phoneNumber, countryCode);
    return formatPhoneNumberByCountry(phone, activeSiteCountry);
}

const getPaymentMethodProviderFromOrder = (order: Order) => {
    // This could be a lot nicer, but ideally we would have the active payment method stored in CT.
    // Right now we have multiple payment methods in the same order, so we need to find the first successful one.
    const firstSuccessfulPayment =
        order?.paymentInfo?.payments
            .filter(
                (payment) =>
                    payment.paymentMethodInfo?.method === "AMAZON_PAY" &&
                    payment?.transactions?.some(
                        (transaction) =>
                            transaction?.type === "Authorization" &&
                            transaction?.state === "Success"
                    )
            )
            .find((payment) => payment) || null;

    if (firstSuccessfulPayment?.paymentMethodInfo?.method === "AMAZON_PAY") {
        return "amazon_pay";
    }
    return "adyen";
};

export const removeSessionIdAndRedirectResultFromUrl = () => {
    if (window?.location) {
        const url = new URL(window.location.href);
        url.searchParams.delete("sessionId");
        url.searchParams.delete("redirectResult");
        url.searchParams.delete("type");
        url.searchParams.delete("resultCode");

        url.searchParams.delete(AMAZON_QUERY_PARAMS.ACTION);
        url.searchParams.delete(AMAZON_QUERY_PARAMS.SESSION_ID);

        window.history.replaceState({}, document.title, url.href);
    }
};

export const getInitialTouchedValues = (
    values: Record<string, unknown>
): Record<string, boolean> => {
    const touched: Record<string, boolean> = {};
    Object.entries(values)?.forEach(([key, value]) => {
        // Empty values should not trigger touched state
        if (value === null || value === "") {
            return;
        }
        // undefined and boolean should not trigger touched state
        if (typeof value === "undefined" || typeof value === "boolean") {
            return;
        }

        touched[key] = true;
    });
    return touched;
};
